import 'preline';

const queuePrelineAutoInit = (() => {
    let isQueued = false;

    return () => {
        if (isQueued) {
            return;
        }

        isQueued = true;

        requestAnimationFrame(() => {
            isQueued = false;
            window.HSStaticMethods?.autoInit();
        });
    };
})();

document.addEventListener('livewire:initialized', () => {
    queuePrelineAutoInit();

    if (!window.Livewire?.hook) {
        return;
    }

    window.Livewire.hook('morph.updated', () => {
        queuePrelineAutoInit();
    });
});

document.addEventListener('livewire:navigated', () => {
    queuePrelineAutoInit();
});

document.addEventListener('submit', (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) {
        return;
    }

    if (form.hasAttribute('wire:submit') || form.dataset.allowMultipleSubmits === 'true') {
        return;
    }

    if (form.dataset.submitted === 'true') {
        event.preventDefault();

        return;
    }

    form.dataset.submitted = 'true';
    form.setAttribute('aria-busy', 'true');

    window.setTimeout(() => {
        form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((button) => {
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
        });
    }, 0);
}, true);

document.addEventListener('alpine:init', () => {

    Alpine.directive('transition-preset', (el, { expression }) => {
        const presets = {
            'slide-left': {
                enter: { duration: 300, easing: 'ease-out', delay: 0, from: { opacity: 0, transform: 'translateX(-1rem)' }, to: { opacity: 1, transform: 'translateX(0)' } },
                leave: { duration: 200, easing: 'ease-in', delay: 0, from: { opacity: 1, transform: 'translateX(0)' }, to: { opacity: 0, transform: 'translateX(-1rem)' } },
            },
            'slide-right': {
                enter: { duration: 300, easing: 'ease-out', delay: 200, from: { opacity: 0, transform: 'translateX(1rem)' }, to: { opacity: 1, transform: 'translateX(0)' } },
                leave: { duration: 200, easing: 'ease-in', delay: 0, from: { opacity: 1, transform: 'translateX(0)' }, to: { opacity: 0, transform: 'translateX(1rem)' } },
            },
        };

        const preset = presets[expression];
        if (!preset) return;

        function buildStage(cfg) {
            return {
                during: { transitionProperty: 'opacity, transform', transitionDuration: `${cfg.duration}ms`, transitionTimingFunction: cfg.easing, transitionDelay: `${cfg.delay || 0}ms` },
                start: cfg.from,
                end: cfg.to,
            };
        }

        function runTransition(el, stage, before, after) {
            Object.assign(el.style, stage.start);
            Object.assign(el.style, stage.during);
            before();
            getComputedStyle(el).opacity;
            requestAnimationFrame(() => {
                Object.assign(el.style, stage.end);
                setTimeout(() => {
                    for (const p of Object.keys({ ...stage.during, ...stage.start, ...stage.end })) {
                        el.style.removeProperty(p.replace(/[A-Z]/g, c => '-' + c.toLowerCase()));
                    }
                    after();
                }, (parseFloat(stage.during.transitionDuration) || 300) + (parseFloat(stage.during.transitionDelay) || 0));
            });
        }

        const enter = buildStage(preset.enter);
        const leave = buildStage(preset.leave);

        el._x_transition = {
            enter,
            leave,
            in(before = () => {}, after = () => {}) {
                runTransition(el, enter, before, after);
            },
            out(before = () => {}, after = () => {}) {
                runTransition(el, leave, before, after);
            },
        };
    });

    Alpine.data('toastNotification', () => ({
        visible: false,
        message: '',
        type: 'success',
        timeout: null,

        show(payload = {}) {
            this.message = payload.message ?? '';
            this.type = payload.type ?? 'success';
            this.visible = true;

            window.clearTimeout(this.timeout);
            this.timeout = window.setTimeout(() => this.hide(), payload.duration ?? 4000);
        },

        hide() {
            this.visible = false;
            window.clearTimeout(this.timeout);
            this.timeout = null;
        },
    }));

    // initialAmount   – suma creditului (lei), ex: 50000
    // initialPeriod   – durata în luni, ex: 60
    // interestAmount  – dobânda nominală anuală %, ex: 8.85
    // monthlyFee      – comision lunar administrare (lei), ex: 20
    // upfrontFee      – comision analiză dosar (lei), ex: 200
    // locale / decimals – pentru formatat lei
    Alpine.data('loanCalculator', (initialAmount = 0, initialPeriod = 0, interestAmount = 0, monthlyFee = 0, upfrontFee = 0, locale = 'ro-RO', decimals = 2) => ({
            amount: Number(initialAmount) || 0,
            period: Number(initialPeriod) || 0,
            interest: Number(interestAmount) || 0, // % pe an
            monthlyFee: Number(monthlyFee) || 0,
            upfrontFee: Number(upfrontFee) || 0,

            locale,
            decimals,

            format(v) {
                return new Intl.NumberFormat(this.locale, {
                    minimumFractionDigits: this.decimals,
                    maximumFractionDigits: this.decimals,
                }).format(Number(v) || 0);
            },

            // dobândă lunară nominală
            get monthlyInterest() {
                if (!this.interest) return 0;
                return (this.interest / 100) / 12;
            },

            // rata lunară DOAR pentru credit (anuitate, fără comision)
            get baseInstallment() {
                const C = this.amount;
                const n = this.period;
                const i = this.monthlyInterest;

                if (!C || !n || !i) return 0;

                const factor = i / (1 - Math.pow(1 + i, -n));
                return C * factor;
            },

            // rata lunară TOTALĂ (credit + comision lunar)
            get monthlyPayment() {
                return this.baseInstallment + this.monthlyFee;
            },

            // valoarea totală plătibilă (rata lunară * luni + comision inițial)
            get totalPayable() {
                if (!this.period) return 0;
                return this.monthlyPayment * this.period + this.upfrontFee;
            },

            // DAE aproximată numeric (anual %, cu două zecimale)
            get dae() {
                const C = this.amount;
                const n = this.period;
                const R = this.monthlyPayment;
                const F0 = this.upfrontFee;

                // Dacă lipsesc date, întoarcem 0
                if (!C || !n || !R) return 0;

                // Suma netă încasată de client la t = 0
                const net = C - F0;

                // Căutăm rata lunară efectivă j astfel încât:
                // net = Σ R / (1 + j)^k, k = 1..n
                let low = 0;
                let high = 1; // 100% / lună e enorm, dar bun ca limită superioară
                const iterations = 60;

                for (let t = 0; t < iterations; t++) {
                    const mid = (low + high) / 2;

                    // calculăm valoarea actualizată a ratelor la rata mid
                    let pv = 0;
                    for (let k = 1; k <= n; k++) {
                        pv += R / Math.pow(1 + mid, k);
                    }

                    if (pv > net) {
                        // Discount prea mic, PV prea mare -> creștem rata
                        low = mid;
                    } else {
                        high = mid;
                    }
                }

                const j = (low + high) / 2;          // rată lunară efectivă
                const daeAnnual = j * 12 * 100;      // % pe an

                return daeAnnual;
            },

            // Pentru compatibilitate cu ce foloseai înainte (`rate`):
            // acum `rate` = rata lunară totală (lei / lună)
            get rate() {
                return this.monthlyPayment;
            },

            // Snap amount to proper step increments
            // From 500-25000: step 100 | From 25000-100000: step 5000
            snapAmount() {
                if (this.amount < 25000) {
                    // Step of 100 below 25000
                    this.amount = Math.round(this.amount / 100) * 100;
                } else {
                    // Step of 5000 above 25000
                    this.amount = Math.round(this.amount / 5000) * 5000;
                }
                // Ensure within bounds
                this.amount = Math.max(500, Math.min(100000, this.amount));
            },
        })
    );

    Alpine.data('typewriter', (text = '', typeSpeed = 150, deleteSpeed = 80, pauseFull = 1500, pauseEmpty = 500) => ({
        text,
        displayed: '',
        index: 0,
        typing: true,
        focused: false,
        init() { this.tick(); },
        tick() {
            if (this.focused) return;
            if (this.typing) {
                if (this.index <= this.text.length) {
                    this.displayed = this.text.substring(0, this.index++);
                    setTimeout(() => this.tick(), typeSpeed);
                } else {
                    setTimeout(() => { this.typing = false; this.tick(); }, pauseFull);
                }
            } else {
                if (this.index > 0) {
                    this.displayed = this.text.substring(0, --this.index);
                    setTimeout(() => this.tick(), deleteSpeed);
                } else {
                    setTimeout(() => { this.typing = true; this.tick(); }, pauseEmpty);
                }
            }
        },
        focus() { this.focused = true; },
        blur() { this.focused = false; this.tick(); },
    }));

    Alpine.data('gdprModal', ($wire) => ({
        autoOpenTimer: null,
        scrollLocked: false,

        init() {
            this.syncAutoOpen();
            this.syncScrollLock();
            this.$watch('$wire.shouldAutoOpen', () => this.syncAutoOpen());
            this.$watch('$wire.autoOpenDelayMs', () => this.syncAutoOpen());
            this.$watch('$wire.gdprModalVisible', () => this.syncScrollLock());
            this.$watch('$wire.gdprModalVisible', () => this.syncAutoOpen());
        },

        syncScrollLock() {
            if ($wire.gdprModalVisible) {
                this.lockScroll();

                return;
            }

            this.unlockScroll();
        },

        syncAutoOpen() {
            if ($wire.shouldAutoOpen && ! $wire.gdprModalVisible && ! this.autoOpenTimer) {
                this.autoOpenTimer = window.setTimeout(() => {
                    this.autoOpenTimer = null;
                    $wire.open();
                }, $wire.autoOpenDelayMs);

                return;
            }

            if ((! $wire.shouldAutoOpen || $wire.gdprModalVisible) && this.autoOpenTimer) {
                this.clearAutoOpenTimer();
            }
        },

        clearAutoOpenTimer() {
            if (! this.autoOpenTimer) {
                return;
            }

            window.clearTimeout(this.autoOpenTimer);
            this.autoOpenTimer = null;
        },

        lockScroll() {
            // if (this.scrollLocked) {
            //     return;
            // }

            // document.documentElement.dataset.gdprModalOverflow = document.documentElement.style.overflow;
            // document.body.dataset.gdprModalOverflow = document.body.style.overflow;
            // document.documentElement.style.overflow = 'hidden';
            // document.body.style.overflow = 'hidden';
            // this.scrollLocked = true;
        },

        unlockScroll() {
            // if (! this.scrollLocked) {
            //     return;
            // }

            // document.documentElement.style.overflow = document.documentElement.dataset.gdprModalOverflow ?? '';
            // document.body.style.overflow = document.body.dataset.gdprModalOverflow ?? '';
            // delete document.documentElement.dataset.gdprModalOverflow;
            // delete document.body.dataset.gdprModalOverflow;
            // this.scrollLocked = false;
        },
    }));

    Alpine.data('otpVerificationModal', ($wire) => ({
        successTimer: null,

        init() {
            this.syncState();

            this.$watch('$wire.shouldNotifyParentOnInit', () => this.syncState());
            this.$watch('$wire.verificationSucceeded', () => this.syncState());
            this.$watch('$wire.showSuccessState', () => this.syncState());
            this.$watch('$wire.successCloseDelayMs', () => this.syncState());
        },

        syncState() {
            if ($wire.shouldNotifyParentOnInit) {
                $wire.notifyVerifiedParent();
            }

            if ($wire.verificationSucceeded && $wire.showSuccessState && $wire.successCloseDelayMs > 0 && ! this.successTimer) {
                this.successTimer = window.setTimeout(() => $wire.completeSuccessState(), $wire.successCloseDelayMs);
            }

            if (! $wire.verificationSucceeded && this.successTimer) {
                window.clearTimeout(this.successTimer);
                this.successTimer = null;
            }
        },
    }));

    Alpine.data('pdfViewer', (primaryUrl, fallbackUrl = null) => {
        // variabile NON-reactive (nu sunt pe `this`)
        let pdfDoc = null;
        let pdfjsLib = null;

        return {
            primaryUrl,
            fallbackUrl,
            activeUrl: null,
            resizeTimeout: null,

            async init() {
                // 1. import pdf.js
                pdfjsLib = await import('https://unpkg.com/pdfjs-dist@4.4.168/build/pdf.mjs');

                pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@4.4.168/build/pdf.worker.min.mjs';

                // 2. încercăm URL-ul principal, apoi fallback (dacă există)
                this.activeUrl = this.primaryUrl;

                const loadDoc = async (url) => pdfjsLib.getDocument(url).promise;

                try {
                    pdfDoc = await loadDoc(this.primaryUrl);
                } catch (e) {
                    console.error('Eroare la încărcarea PDF-ului principal:', e);

                    if (this.fallbackUrl) {
                        console.warn('Încerc fallback PDF:', this.fallbackUrl);
                        this.activeUrl = this.fallbackUrl;
                        pdfDoc = await loadDoc(this.fallbackUrl);
                    } else {
                        throw e;
                    }
                }

                // 3. randăm paginile
                await this.renderPages();

                // 4. re-render la resize (debounced)
                window.addEventListener('resize', () => {
                    clearTimeout(this.resizeTimeout);
                    this.resizeTimeout = setTimeout(() => {
                        this.renderPages();
                    }, 200);
                });
            },

            async renderPages() {
                if (!pdfDoc) return;

                const container = this.$refs.pdfContainer;
                if (!container) return;

                // golim conținutul anterior
                container.innerHTML = '';

                const totalPages = pdfDoc.numPages;

                // lățimea de referință pentru fit-to-width
                const containerWidth =
                    container.clientWidth ||
                    (container.parentElement ? container.parentElement.clientWidth : 800);

                for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
                    const page = await pdfDoc.getPage(pageNumber);

                    // viewport la scale 1
                    const unscaledViewport = page.getViewport({ scale: 1 });

                    // scale ca să încapă pe lățimea containerului
                    const scale = containerWidth / unscaledViewport.width;
                    const viewport = page.getViewport({ scale });

                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    canvas.className = 'mb-4 bg-white block';

                    container.appendChild(canvas);

                    await page.render({ canvasContext: ctx, viewport }).promise;
                }
            },
        };
    });

});
